"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLoginFlow } from "@login";
import {
  Button,
  Input,
  PasswordInput,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "visuais";
import { LogIn, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

interface SimpleAuthFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
}

interface AuthFormData {
  username: string;
  password: string;
}

export function SimpleAuthForm({
  onSuccess,
  onCancel,
  className,
}: SimpleAuthFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const form = useForm<AuthFormData>({
    defaultValues: { username: "", password: "" },
    mode: "onChange",
  });

  const { loginAll } = useLoginFlow({
    username: form.watch("username"),
  });

  const onSubmit = form.handleSubmit(async (data) => {
    setIsLoading(true);

    try {
      const result = await loginAll({
        username: data.username,
        password: data.password,
      });

      if (result) {
        toast.success("✅ Login realizado com sucesso!");
        onSuccess?.();
      } else {
        toast.error("❌ Usuário ou senha inválidos");
      }
    } catch (error) {
      console.error("💥 [SimpleAuthForm] Erro durante login:", error);
      toast.error("❌ Erro ao fazer login. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <div className={`space-y-4 p-4 bg-muted/30 rounded-lg border ${className}`}>
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <LogIn className="h-5 w-5 text-amber-500" />
          <h3 className="text-sm font-medium">Sessão expirada</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Faça login novamente para continuar
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-3">
          <FormField
            control={form.control}
            name="username"
            rules={{ required: "Usuário é obrigatório" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Usuário</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Digite seu usuário"
                    className="text-xs h-8"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            rules={{ required: "Senha é obrigatória" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Senha</FormLabel>
                <FormControl>
                  <PasswordInput
                    {...field}
                    placeholder="Digite sua senha"
                    className="text-xs h-8"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />

          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              size="sm"
              className="flex-1 text-xs h-8"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  <LogIn className="h-3 w-3 mr-1" />
                  Entrar
                </>
              )}
            </Button>

            {onCancel && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-xs h-8"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
